/// <reference lib="dom" />
/**
 * Canvas2D sprite-frame draw helpers — Nova, enemies, frame-source-rect math.
 * Web-only (.web.ts) — never bundled on native. Read-only, no gameState mutation.
 * @module webSpriteDraw
 */

import type { NovaRuntime } from '../engine/gameState';
import {
  NOVA_SHEET,
  NOVA_CLIPS,
  NOVA_DRAW_OFFSET,
  advanceAnim,
} from './SpriteRenderer';
import type { SpriteSheet, AnimClip } from './SpriteRenderer';
import type { DrawOffset } from './drawOffsets';
import type { NovaAnimState } from '../engine/gameState';

/** Per-entity animation state — caller owns the storage (typically a Map). */
export interface AnimState {
  frameIndex: number;
  elapsed: number;
}

// ── Sprite sheet normalisation ─────────────────────────────────────────────
// DECISION: AI-generated sprite sheets have inconsistent per-frame content
// placement (content centre can shift 9+ px between frames). Instead of
// adjusting the draw position (which still crops different source pixels),
// we pre-process the sheet into a corrected canvas at first use: each frame's
// content is extracted, centred, and redrawn at a uniform position. The
// corrected canvas replaces the original image as the draw source.

const _normalizedCache = new Map<HTMLImageElement, HTMLCanvasElement>();

/**
 * Create a normalised copy of a sprite sheet where every frame's content
 * is centred at the same horizontal position. The result is cached.
 * Supports both single-row strips and multi-row grids.
 *
 * @param img    - The original sprite sheet image.
 * @param frameW - Width of a single frame in pixels.
 * @param frameH - Height of a single frame in pixels.
 * @returns A canvas with the same dimensions, usable as a drawImage source.
 */
export function getNormalizedSheet(
  img: HTMLImageElement,
  frameW: number,
  frameH: number,
): HTMLCanvasElement {
  const cached = _normalizedCache.get(img);
  if (cached) return cached;

  // DECISION: Grid-aware normalisation — compute cols/rows from image dims.
  const cols = Math.floor(img.naturalWidth / frameW);
  const rows = Math.floor(img.naturalHeight / frameH);
  const frameCount = cols * rows;
  const out = document.createElement('canvas');
  out.width = img.naturalWidth;
  out.height = img.naturalHeight;
  const outCtx = out.getContext('2d');
  if (!outCtx) { _normalizedCache.set(img, out); return out; }
  // DECISION: Nearest-neighbour for pixel art — bilinear blurs scaled frames.
  outCtx.imageSmoothingEnabled = false;

  // Temporary canvas for per-frame analysis.
  const tmp = document.createElement('canvas');
  tmp.width = img.naturalWidth;
  tmp.height = img.naturalHeight;
  const tmpCtx = tmp.getContext('2d');
  if (!tmpCtx) { outCtx.drawImage(img, 0, 0); _normalizedCache.set(img, out); return out; }
  tmpCtx.drawImage(img, 0, 0);

  const targetCentreX = frameW / 2;

  // DECISION: Two-pass normalisation. Pass 1 finds each frame's bounds and
  // the median bottom row across all frames. Pass 2 draws each frame
  // shifted so content is horizontally centred and vertically bottom-aligned
  // to a consistent foot row. Using the median (not max) avoids outlier
  // frames (e.g. death explosions) skewing the alignment.
  interface FrameBounds { left: number; right: number; top: number; bottom: number }
  const bounds: (FrameBounds | null)[] = [];
  const bottomValues: number[] = [];

  for (let f = 0; f < frameCount; f++) {
    const col = f % cols;
    const row = Math.floor(f / cols);
    const fx = col * frameW;
    const fy = row * frameH;
    const data = tmpCtx.getImageData(fx, fy, frameW, frameH).data;

    let left = frameW;
    let right = -1;
    let top = frameH;
    let bottom = -1;
    for (let r = 0; r < frameH; r++) {
      for (let c = 0; c < frameW; c++) {
        if (data[(r * frameW + c) * 4 + 3] > 10) {
          if (c < left) left = c;
          if (c > right) right = c;
          if (r < top) top = r;
          if (r > bottom) bottom = r;
        }
      }
    }

    if (right < 0) { bounds.push(null); continue; }
    bounds.push({ left, right, top, bottom });
    bottomValues.push(bottom);
  }

  // DECISION: Use median bottom and median content height to resist outlier
  // frames (e.g. frame 47 extends to row 58, frame 20 is 38px tall while
  // most frames are ~28px). Frames taller than the median are scaled down
  // so the character appears a consistent size across all animation states.
  bottomValues.sort((a, b) => a - b);
  const targetBottom = bottomValues.length > 0
    ? bottomValues[Math.floor(bottomValues.length / 2)]
    : frameH - 1;

  const heightValues = bounds
    .filter((b): b is FrameBounds => b !== null)
    .map(b => b.bottom - b.top + 1)
    .sort((a, b) => a - b);
  const medianH = heightValues.length > 0
    ? heightValues[Math.floor(heightValues.length / 2)]
    : frameH;

  // Pass 2: draw each frame centred horizontally, bottom-aligned vertically,
  // and scaled down if content height exceeds the median.
  for (let f = 0; f < frameCount; f++) {
    const b = bounds[f];
    if (!b) continue;

    const col = f % cols;
    const row = Math.floor(f / cols);
    const fx = col * frameW;
    const fy = row * frameH;

    const contentW = b.right - b.left + 1;
    const contentH = b.bottom - b.top + 1;

    // DECISION: Scale by content HEIGHT (not maxDim) and only when the
    // frame is taller than the median. This squashes oversized frames
    // (FALL/LAND dust, idle frames with hair tufts) down to a consistent
    // body height, eliminating per-frame size flicker. Frames shorter
    // than the median (CROUCH, STOMP, DEAD) are left at their natural
    // height so intentional pose changes are preserved. Width is scaled
    // proportionally so the character keeps its aspect ratio without
    // squishing during wide poses (attacks, arm extensions).
    const scale = contentH > medianH ? medianH / contentH : 1;
    const drawW = Math.round(contentW * scale);
    const drawH = Math.round(contentH * scale);

    // Centre horizontally within the frame.
    const drawCentreX = targetCentreX;
    const destX = fx + Math.round(drawCentreX - drawW / 2);
    // Bottom-align feet to targetBottom row.
    const destY = fy + targetBottom - drawH + 1;
    // Clamp to frame boundaries to avoid bleeding into adjacent frames.
    const clampedDestX = Math.max(fx, Math.min(destX, fx + frameW - drawW));
    const clampedDestY = Math.max(fy, Math.min(destY, fy + frameH - drawH));
    outCtx.drawImage(
      img,
      fx + b.left, fy + b.top, contentW, contentH,
      clampedDestX, clampedDestY, drawW, drawH,
    );
  }

  _normalizedCache.set(img, out);
  return out;
}

/**
 * Draw a sprite frame with optional horizontal flip for facing-left entities.
 * Accepts either an HTMLImageElement or HTMLCanvasElement as the source.
 * Supports both single-row strips and multi-row grid sheets.
 */
export function drawSpriteFrame(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement | HTMLCanvasElement,
  frameW: number,
  frameH: number,
  frameIndex: number,
  screenX: number,
  screenY: number,
  destW: number,
  destH: number,
  facingRight: boolean,
): void {
  const w = img instanceof HTMLCanvasElement ? img.width : img.naturalWidth;
  const h = img instanceof HTMLCanvasElement ? img.height : img.naturalHeight;
  if (!w) return;
  // DECISION: Grid-aware source rect — cols from width, row from index.
  // For single-row sheets (h === frameH), row is always 0.
  const cols = Math.floor(w / frameW);
  const totalFrames = cols * Math.floor(h / frameH);
  const idx = Math.min(frameIndex, totalFrames - 1);
  const sx = (idx % cols) * frameW;
  const sy = Math.floor(idx / cols) * frameH;
  if (facingRight) {
    ctx.drawImage(img, sx, sy, frameW, frameH, screenX, screenY, destW, destH);
  } else {
    ctx.save();
    ctx.translate(screenX + destW, screenY);
    ctx.scale(-1, 1);
    ctx.drawImage(img, sx, sy, frameW, frameH, 0, 0, destW, destH);
    ctx.restore();
  }
}

/**
 * Draw the player character at its current position with the appropriate
 * animation frame and facing direction. Supports any character's sprite sheet.
 * No-op when `visible` is false (used for hurt-blink).
 *
 * @param ctx        - Canvas 2D rendering context.
 * @param charImg    - The character's loaded sprite sheet image.
 * @param nova       - The player runtime state (position, state, facing).
 * @param anim       - Mutable animation timer (elapsed, frameIndex).
 * @param dt         - Delta time this tick.
 * @param camX       - Camera X offset.
 * @param camY       - Camera Y offset.
 * @param visible    - False to skip rendering (hurt-blink).
 * @param charSheet  - Sprite sheet descriptor for the active character (optional, defaults to Nova).
 * @param charClips  - Animation clip mapping for the active character (optional, defaults to Nova).
 * @param charOffset - Draw offset for the active character (optional, defaults to Nova).
 */
export function drawNovaSprite(
  ctx: CanvasRenderingContext2D,
  charImg: HTMLImageElement,
  nova: NovaRuntime,
  anim: AnimState,
  dt: number,
  camX: number,
  camY: number,
  visible: boolean,
  charSheet?: SpriteSheet,
  charClips?: Record<NovaAnimState, AnimClip>,
  charOffset?: DrawOffset,
): void {
  if (!visible) return;

  const sheet = charSheet ?? NOVA_SHEET;
  const clips = charClips ?? NOVA_CLIPS;
  const offset = charOffset ?? NOVA_DRAW_OFFSET;

  const clip = clips[nova.state] ?? clips.IDLE;
  const next = advanceAnim(anim.elapsed, clip, dt);
  anim.elapsed = next.elapsed;
  anim.frameIndex = next.frameIndex;

  // DECISION: Draw from the normalised canvas so every frame's content is
  // centred identically — eliminates horizontal jitter from the source sheet.
  const normalised = getNormalizedSheet(charImg, sheet.frameWidth, sheet.frameHeight);

  const drawX = Math.round(nova.x - camX + offset.x);
  const drawY = Math.round(nova.y - camY + offset.y);
  drawSpriteFrame(
    ctx,
    normalised,
    sheet.frameWidth,
    sheet.frameHeight,
    anim.frameIndex,
    drawX,
    drawY,
    offset.w,
    offset.h,
    nova.facingRight,
  );
}

// ── Enemy drawing extracted to webEnemyDraw.web.ts ───────────────────────

// Re-export for backward compatibility with existing imports.
export { drawEnemySprite } from './webEnemyDraw';